<template>
  <div class="card">
    <div class="card-body">
      <form @submit.prevent="submit">
        <div class="row">
          <!-- Customer Selection -->
          <div class="col-lg-4 col-sm-6 col-12">
            <div class="input-blocks">
              <label class="form-label">Customer</label>
              <div class="row">
                <div class="col-lg-10 col-sm-10 col-10">
                  <select
                    v-model="form.selectedCustomer"
                    class="form-control"
                    :class="{ 'is-invalid': errors.selectedCustomer }"
                    @change="onCustomerChange"
                  >
                    <option value="">Choose</option>
                    <option
                      v-for="customer in customers"
                      :key="customer.id"
                      :value="customer.id"
                      :data-phone="customer.phone"
                    >
                      {{ customer.name }}
                    </option>
                  </select>
                  <div v-if="errors.selectedCustomer" class="text-danger">
                    {{ errors.selectedCustomer[0] }}
                  </div>
                </div>
                <div class="col-lg-2 col-sm-2 col-2 ps-0">
                  <div class="add-icon">
                    <a href="#" @click.prevent="openCustomerModal" class="choose-add">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                        viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                        stroke-linecap="round" stroke-linejoin="round"
                        class="feather feather-plus-circle plus">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="16"></line>
                        <line x1="8" y1="12" x2="16" y2="12"></line>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Date -->
          <div class="col-lg-4 col-sm-6 col-12">
            <div class="input-blocks">
              <label>Date</label>
              <div class="input-groupicon">
                <input
                  v-model="form.date"
                  type="date"
                  class="form-control"
                  :class="{ 'is-invalid': errors.date }"
                >
                <div v-if="errors.date" class="text-danger">
                  {{ errors.date[0] }}
                </div>
              </div>
            </div>
          </div>

          <!-- Product Search -->
          <div class="col-lg-12 col-sm-6 col-12">
            <div class="input-blocks">
              <label>Product Name</label>
              <div class="input-groupicon position-relative">
                <input
                  v-model="productSearch"
                  @input="searchProducts"
                  type="text"
                  placeholder="Please type product code and select"
                  class="form-control"
                  @focus="showProductList()"
                  @blur="showProductList()"
                >

                <!-- Search Results Dropdown -->
                <div
                  v-if="searchResults.length > 0 && showProduct"
                  class="position-absolute w-100 mt-1 bg-white border rounded shadow-sm"
                  style="z-index: 1000; overflow-y: scroll;height: 350px;"
                >
                  <div
                    v-for="product in searchResults"
                    :key="`${product.id}-${product.batch_no}`"
                    class="p-2 border-bottom hover-bg-gray cursor-pointer"
                    @click="selectProduct(product.id, product.batch_no)"
                  >
                    <div>{{ product.name }}</div>
                    <small class="text-muted">
                      Code: {{ product.code }} |
                      Stock: {{ product.quantity }} |
                      Batch: {{ product.batch_no }} |
                      Price: ${{ parseFloat(product.price).toFixed(2) }}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Items Table -->
        <div class="table-responsive">
          <table class="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th style="width: 100px">Qty</th>
                <th v-if="canViewCost" style="width: 200px">Cost</th>
                <th style="width: 200px">Price</th>
                <th>Total</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in form.items" :key="index">
                <td>{{ item.name }}</td>
                <td>{{ item.code }}</td>
                <td>
                  <input
                    v-model.number="item.qty"
                    @input="updateQuantity(index, $event.target.value)"
                    type="number"
                    min="1"
                    class="form-control"
                    :class="{ 'is-invalid': errors[`items.${index}.qty`] }"
                  >
                  <div v-if="errors[`items.${index}.qty`]" class="text-danger">
                    {{ errors[`items.${index}.qty`][0] }}
                  </div>
                </td>
                <td v-if="canViewCost">
                  {{ parseFloat(item.cost).toFixed(2) }}
                </td>
                <td>
                  <input
                    v-model.number="item.price"
                    @input="updatePrice(index, $event.target.value)"
                    type="number"
                    step="0.01"
                    min="0"
                    class="form-control"
                  >
                </td>
                <td>${{ parseFloat(item.total).toFixed(2) }}</td>
                <td>
                  <button
                    type="button"
                    @click="removeItem(index)"
                    class="btn btn-sm btn-danger"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="errors.items" class="text-danger">
            {{ errors.items[0] }}
          </div>
        </div>

        <!-- Totals Section -->
        <div class="row">
          <div class="col-lg-6 ms-auto">
            <div class="total-order w-100 max-widthauto m-auto mb-4">
              <ul>
                <li>
                  <h4>Total</h4>
                  <h5>${{ totals.total.toFixed(2) }}</h5>
                </li>
                <li>
                  <h4>Discount</h4>
                  <h5>${{ totals.discountAmount.toFixed(2) }}</h5>
                </li>
                <li>
                  <h4>Grand Total</h4>
                  <h5>${{ totals.grandTotal.toFixed(2) }}</h5>
                </li>
                <li>
                  <h4>Paid</h4>
                  <h5>${{ parseFloat(form.paid || 0).toFixed(2) }}</h5>
                </li>
                <li>
                  <h4>Due</h4>
                  <h5>${{ totals.due.toFixed(2) }}</h5>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Input Fields -->
        <div class="row">
          <div class="col-lg-4 col-sm-6 col-12">
            <div class="input-blocks">
              <label>Total Qty</label>
              <div class="input-groupicon">
                <input
                  :value="totals.totalQty"
                  type="text"
                  readonly
                  class="form-control"
                >
              </div>
            </div>
          </div>

          <div class="col-lg-4 col-sm-6 col-12">
            <div class="input-blocks">
              <label>Discount %</label>
              <div class="input-groupicon">
                <input
                  v-model.number="form.discount"
                  @input="calculateTotals"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  class="form-control"
                >
              </div>
            </div>
          </div>

          <div class="col-lg-4 col-sm-6 col-12">
            <div class="input-blocks">
              <label>Paid</label>
              <div class="input-groupicon">
                <input
                  v-model.number="form.paid"
                  @input="calculateTotals"
                  type="number"
                  min="0"
                  step="0.01"
                  class="form-control"
                  :class="{ 'is-invalid': errors.paid }"
                >
                <div v-if="errors.paid" class="text-danger">
                  {{ errors.paid[0] }}
                </div>
              </div>
            </div>
          </div>

          <div class="col-lg-12 text-end">
            <button type="button" class="btn btn-cancel me-3" @click="cancelForm">
              Cancel
            </button>
            <button type="submit" class="btn btn-submit" :disabled="loading">
              {{ loading ? 'Submitting...' : 'Submit' }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { showSuccessMessage, showErrorMessage } from '../utils/notifications.js';

export default {
  name: 'SalesForm',
  props: {
    initialCustomers: {
      type: Array,
      default: () => []
    },
    shopId: {
      type: [Number, String],
      required: true
    },
    canViewCost: {
      type: Boolean,
      default: false
    }
  },
  data() {

    return {
      form: {
        selectedCustomer: '',
        date: new Date().toISOString().split('T')[0],
        items: [],
        discount: 0,
        paid: 0
      },
      customers: [...this.initialCustomers],
      productSearch: '',
      searchResults: [],
      initalProducts: [],
      errors: {},
      loading: false,
      searchTimeout: null,
      showProduct: false
    }
  },
  computed: {
    totals() {
      const total = this.form.items.reduce((sum, item) => sum + parseFloat(item.total || 0), 0);
      const totalQty = this.form.items.reduce((sum, item) => sum + parseInt(item.qty || 0), 0);
      const discountAmount = total * (parseFloat(this.form.discount || 0) / 100);
      const grandTotal = total - discountAmount;
      const due = grandTotal - parseFloat(this.form.paid || 0);

      return {
        total,
        totalQty,
        discountAmount,
        grandTotal,
        due
      };
    }
  },
  methods: {
    showProductList() {
      setTimeout(() => {
        this.showProduct = !this.showProduct;
      }, 300);

    },

    async searchProducts() {
      if (this.productSearch.length < 2) {
        this.searchResults = this.initalProducts;
        return;
      }

      // Debounce search
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(async () => {
        try {
          const response = await this.$http.get('/api/products/search', {
            params: {
              q: this.productSearch,
              shop_id: this.shopId
            }
          });
          this.searchResults = response.data.products || [];
        } catch (error) {
          console.error('Product search failed:', error);
          this.searchResults = [];
        }
      }, 300);
    },

    async selectProduct(productId, batchNo) {
      try {
        // Check if product already exists in items
        const existingIndex = this.form.items.findIndex(
          item => item.product_id === productId && item.batch_no === batchNo
        );

        if (existingIndex !== -1) {
          this.showErrorMessage('Product already added to the list!');
          return;
        }

        const response = await this.$http.get(`/api/products/${productId}/stock`, {
          params: { batch_no: batchNo, shop_id: this.shopId }
        });

        const stockData = response.data.stock;
        if (!stockData || stockData.quantity <= 0) {
          this.showErrorMessage('Product out of stock!');
          return;
        }

        // Add product to items
        this.form.items.push({
          product_id: productId,
          name: stockData.product_name,
          code: stockData.product_code,
          batch_no: batchNo,
          qty: 1,
          price: parseFloat(stockData.unit_price),
          cost: parseFloat(stockData.unit_cost),
          total: parseFloat(stockData.unit_price),
          available_qty: stockData.quantity
        });

        this.productSearch = '';
        this.searchResults = this.initalProducts;
        this.calculateTotals();

      } catch (error) {
        console.error('Error selecting product:', error);
        this.showErrorMessage('Failed to add product');
      }
    },

    updateQuantity(index, qty) {
      const quantity = parseInt(qty) || 1;
      const item = this.form.items[index];

      if (quantity > item.available_qty) {
        this.showErrorMessage('Requested quantity exceeds available stock!');
        this.form.items[index].qty = item.available_qty;
        return;
      }

      this.form.items[index].qty = quantity;
      this.form.items[index].total = quantity * parseFloat(item.price);
      this.calculateTotals();
    },

    updatePrice(index, price) {
      const newPrice = parseFloat(price) || 0;
      if (newPrice <= 0) return;

      this.form.items[index].price = newPrice;
      this.form.items[index].total = this.form.items[index].qty * newPrice;
      this.calculateTotals();
    },

    removeItem(index) {
      this.form.items.splice(index, 1);
      this.calculateTotals();
    },

    calculateTotals() {
      // Totals are computed automatically via computed property
      // This method exists for compatibility and to trigger reactivity
    },

    onCustomerChange() {
      // Handle customer selection if needed
    },

    openCustomerModal() {
      // Trigger customer modal
      const modal = new bootstrap.Modal(document.getElementById('add-customer-vue'));
      modal.show();
    },

    async submit() {
      this.loading = true;
      this.errors = {};

      try {
        const saleData = {
          customer_id: this.form.selectedCustomer,
          date: this.form.date,
          items: this.form.items.map(item => ({
            product_id: item.product_id,
            batch_no: item.batch_no,
            qty: item.qty,
            price: item.price,
            cost: item.cost
          })),
          discount: this.form.discount || 0,
          paid: this.form.paid || 0,
          shop_id: this.shopId
        };

        const response = await this.$http.post('/api/sales', saleData);

        this.showSuccessMessage('Sale created successfully!');
        this.resetForm();

        // Emit success event
        this.$emit('sale-created', response.data);

        // Redirect or refresh
        if (response.data.redirect_url) {
          setTimeout(() => {
            window.location.href = response.data.redirect_url;
          }, 1500);
        }

      } catch (error) {
        console.error('Sale creation error:', error);

        if (error.response && error.response.status === 422) {
          this.errors = error.response.data.errors;
        } else {
          this.showErrorMessage('An error occurred while creating the sale.');
        }
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.form = {
        selectedCustomer: '',
        date: new Date().toISOString().split('T')[0],
        items: [],
        discount: 0,
        paid: 0
      };
      this.errors = {};
    },

    cancelForm() {
      this.resetForm();
      this.$emit('cancel');
    },

    async loadCustomers() {
      try {
        const response = await this.$http.get('/api/customers');
        this.customers = response.data.customers || [];
      } catch (error) {
        console.error('Failed to load customers:', error);
      }
    },

    async initialProducts() {
      try {
        const response = await this.$http.get('/api/products/search', {
             params: {
              shop_id: this.shopId
            }
        });
        this.initalProducts = response.data.products || [];
        this.searchResults = this.initalProducts;
      } catch (error) {
        console.error('Failed to load products:', error);
      }
    },

    showSuccessMessage,
    showErrorMessage
  },

  async mounted() {
    console.log('SalesForm component mounted');

    // Load customers if not provided
    if (this.customers.length === 0) {
      await this.loadCustomers();
    }

    // Load products if not provided
    if (this.searchResults.length === 0) {
      await this.initialProducts();
    }

    // Listen for customer creation events
    document.addEventListener('customer-created', (event) => {
      this.loadCustomers();

      if (event.detail && event.detail.customer) {
        this.form.selectedCustomer = event.detail.customer.id;
      }
    });

    // Mark element as mounted
    this.$el.closest('[data-vue-component]')?.classList.add('vue-mounted');
  }
}
</script>

<style scoped>
.hover-bg-gray:hover {
  background-color: #f8f9fa;
}

.cursor-pointer {
  cursor: pointer;
}

.total-order ul {
  list-style: none;
  padding: 0;
}

.total-order ul li {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #eee;
}

.total-order ul li:last-child {
  border-bottom: none;
  font-weight: bold;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.text-danger {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}
</style>
